import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError, fmtDT } from "@/lib/platform";
import { slug } from "@/lib/rights";
import { Panel, VerifyBadge } from "@/components/rights/RightsBadges";
import { EvidenceUploadButton } from "@/components/rights/EvidenceUploadButton";

const RecordForm = ({ r, methods, onDone }) => {
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const record = async (result) => {
    setBusy(true);
    try {
      await api.post(`/rights/${r.id}/verifications`, { method, result, notes });
      toast.success(`${method} recorded as ${result.toLowerCase()}`);
      setMethod(""); setNotes("");
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not record check")); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="verification-form">
      <Select value={method} onValueChange={setMethod}>
        <SelectTrigger className="h-9 w-[180px] text-xs bg-white" data-testid="verification-method-select"><SelectValue placeholder="Method" /></SelectTrigger>
        <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
      </Select>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was checked (e.g. OTP ref, ID last 4)"
             className="h-9 flex-1 min-w-[200px] text-xs bg-white" data-testid="verification-notes" />
      <Button size="sm" disabled={!method || busy} onClick={() => record("VERIFIED")} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="verification-pass-button"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verified</Button>
      <Button size="sm" variant="outline" disabled={!method || busy} onClick={() => record("FAILED")} className="text-red-600"
              data-testid="verification-fail-button"><XCircle className="w-3.5 h-3.5 mr-1" /> Failed</Button>
    </div>
  );
};

export const VerificationPanel = ({ r, canEdit, onChanged }) => {
  const { methods, required_checks: req, verified_methods: ok } = r.checklist;
  const open = ["RECEIVED", "VERIFICATION"].includes(r.status);
  return (
    <Panel eyebrow={`${r.subject_type_label} identity`} title="Identity verification" testId="verification-panel"
           actions={<VerifyBadge status={r.verification_status} testId="verification-status" />}>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="verification-methods">
        {methods.map((m) => {
          const passed = ok.includes(m);
          return (
            <span key={m} data-testid={`verification-method-${slug(m)}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs ${
                    passed ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}>
              <ShieldCheck className={`w-3.5 h-3.5 ${passed ? "text-emerald-600" : "text-slate-300"}`} /> {m}
            </span>
          );
        })}
        <span className="text-[11px] text-slate-500 self-center ml-1">{ok.length}/{req} required check{req > 1 ? "s" : ""} passed</span>
      </div>
      {canEdit && open && <RecordForm r={r} methods={methods} onDone={onChanged} />}
      <div className="mt-4 divide-y divide-slate-100" data-testid="verification-log">
        {r.verifications.length === 0 && <div className="text-xs text-slate-500 py-3">No checks recorded yet.</div>}
        {r.verifications.map((v) => (
          <div key={v.id} className="py-2.5 flex items-center gap-3" data-testid={`verification-${v.id}`}>
            {v.result === "VERIFIED" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#003366]">{v.method} <span className="text-xs font-normal text-slate-500">· {v.result.toLowerCase()}</span></div>
              <div className="text-[11px] text-slate-500 truncate">{v.verified_by} · {fmtDT(v.verified_at)}{v.notes && ` · ${v.notes}`}</div>
            </div>
            <span className="text-[10px] font-mono text-slate-400">{v.evidence_file_ids?.length || 0} file(s)</span>
            {canEdit && (
              <EvidenceUploadButton requestId={r.id} category="VERIFICATION" verificationId={v.id} onUploaded={onChanged}
                                    testId={`verification-attach-${v.id}`} />
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
};
